import { powerSyncCollectionOptions } from "@tanstack/powersync-db-collection";
import { createCollection } from "@tanstack/react-db";
import { UnitReadinessSchema } from "@vidimo/shared";
import { z } from "zod";
import { AppSchema } from "@/lib/powersync-schema.generated";
import { collectionBase } from "./_factory";
import { nullishToNull } from "./nullish";
import { serverEnum } from "./server-enum";
import { syncedRow } from "./synced-row";

export const SelectUnitSchema = syncedRow({
  amenityIds: z.string().nullable(),
  code: z.string(),
  createdAt: z.string(),
  description: z.string().nullable(),
  extraBedsCribs: z.number().nullable(),
  extraBedsRollaways: z.number().nullable(),
  id: z.string(),
  maxAdults: z.number().nullable(),
  maxChildren: z.number().nullable(),
  maxChildrenPayable: z.number().nullable(),
  maxGuests: z.number().nullable(),
  name: z.string(),
  propertyId: z.string(),
  // `serverEnum`, not a bare `z.string()`: a `units` row already on a device
  // from before this column joined the sync rules reads `readiness` back as SQL
  // NULL, and a strict parse drops every such row — leaving the app with no
  // rooms at all. `dirty` is the fallback because it is the
  // conservative reading: a stale row must not assure a host a room is ready.
  readiness: serverEnum(UnitReadinessSchema, "dirty"),
  readinessUpdatedAt: z.string().nullable(),
  sizeUnit: z.string().nullable(),
  sizeValue: z.number().nullable(),
  smokingPolicy: z.string().nullable(),
  unitTypeId: z.string().nullable(),
  updatedAt: z.string(),
}).transform((row) => {
  let parsedAmenityIds: string[] | undefined;
  if (row.amenityIds) {
    try {
      parsedAmenityIds = JSON.parse(row.amenityIds);
    } catch {
      parsedAmenityIds = undefined;
    }
  }

  return {
    ...nullishToNull(row),
    amenityIds: parsedAmenityIds,
  };
});
// The transform must only reshape real columns (amenityIds JSON → array),
// never add keys: the parsed object is what gets persisted on writes, and a
// key without a backing SQLite column (a "phantom field") makes every
// insert/update on the collection throw and roll back.

/**
 * Re-encode `amenityIds` for a `unitsCollection.update`.
 *
 * **Every update must call this, including one that never touches amenities.**
 * The transform above is one-way: TanStack re-validates the whole post-transform
 * draft on every `.update`, and that draft carries the parsed `string[]` while
 * this schema expects the JSON string SQLite stores. A partial update therefore
 * fails validation on a column the caller never meant to write and — because a
 * rejected optimistic write surfaces nothing — persists silently nothing.
 * `unit-form` escaped it only because its submit reassigns the column every
 * save; the readiness control, which writes one field, was a complete no-op
 * until this existed.
 *
 * The sanctioned remedy for this class is the two-schema split
 * (`deserializationSchema` + `schema`) that `guest-chats.ts` uses for the same
 * JSON-text-to-array shape. It is NOT used here: `units` is the one collection
 * whose in-memory schema needs an optional array, and an optional key makes
 * `powerSyncCollectionOptions` stop inferring the row type from the schema —
 * every column on every collection then degrades to the raw SQLite type (the
 * trap `server-enum.ts` documents for `.nullish()`). Converting `units` to the
 * split therefore means giving `amenityIds` a non-optional `[]`, which changes
 * what an empty selection writes to the column from NULL to `"[]"`. That is a
 * stored-shape change, so it belongs in its own change with its own coverage,
 * not in this one.
 *
 * Typed `unknown` on purpose: at compile time a draft's `amenityIds` is the
 * INPUT type (`string | null`) while at runtime it holds the array, so a
 * narrower signature would be a lie in one direction or the other.
 */
export function encodeAmenityIds(value: unknown): string | null {
  if (Array.isArray(value)) {
    return value.length > 0 ? JSON.stringify(value) : null;
  }
  return typeof value === "string" ? value : null;
}

export type Unit = z.infer<typeof SelectUnitSchema>;

export const unitsCollection = createCollection(
  powerSyncCollectionOptions({
    ...collectionBase("unit"),
    schema: SelectUnitSchema,
    table: AppSchema.props.units,
  })
);
